import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

// CSS animation for the "You" marker
const pulseAnimation = `
  @keyframes pulse {
    0%, 100% { transform: scale(0.7); opacity: 1; }
    50%      { transform: scale(1.2); opacity: 0.7; }
  }

  .you-marker {
    animation: pulse 2s infinite ease-in-out;
  }
`;

const safeNumber = (value, defaultValue = 0) => {
  if (typeof value === 'number' && !isNaN(value)) {
    return value;
  }
  const parsed = parseFloat(value);
  return !isNaN(parsed) ? parsed : defaultValue;
};

const formatPercentage = (value) => {
  const num = safeNumber(value);
  return Math.round(num);
};

/**
 * ResilienceSection - Specialized component for displaying resilience construct data
 *
 * This component provides a unique UI for the Resilience construct, featuring:
 * - Left side: Quadrant visualization showing attributional style based on positive/negative percentiles
 * - Right side: Personalized passages with insights based on the user's quadrant position
 *
 * The component safely handles data extraction from Redux contextData and provides
 * fallback values for missing or invalid data.
 *
 * @returns {JSX.Element} The resilience dashboard with quadrant chart and personalized content
 */
const ResilienceSection = () => {
  const { contextData } = useSelector((state) => state.studentPerformenceConstruct);
  const [isYouTextOverlaping, setIsYouTextOverlaping] = useState('')
  const youRef = useRef(null);
  const textRefs = {
    optimistic: useRef(null),
    resilient: useRef(null),
    pessimistic: useRef(null),
    selfCritical: useRef(null),
  };

  useEffect(() => {
    const detectOverlap = () => {
      const youEl = youRef.current?.getBoundingClientRect();
      if (!youEl) return;

      let foundOverlap = null;

      Object.entries(textRefs).forEach(([key, ref]) => {
        const rect = ref.current?.getBoundingClientRect();
        if (!rect) return;

        const isOverlap = !(
          youEl.right < rect.left ||
          youEl.left > rect.right ||
          youEl.bottom < rect.top ||
          youEl.top > rect.bottom
        );

        if (isOverlap) {
          foundOverlap = key;
        }
      });

      setIsYouTextOverlaping(foundOverlap); // agar kuch overlap hua toh wahi, warna null
    };

    detectOverlap();
    window.addEventListener("resize", detectOverlap);
    window.addEventListener("scroll", detectOverlap);

    return () => {
      window.removeEventListener("resize", detectOverlap);
      window.removeEventListener("scroll", detectOverlap);
      setIsYouTextOverlaping('')
    };
  }, [contextData]);

  const resilienceData = useMemo(() => {
    if (!contextData) return null;

    const { construct_label = [], behaviors = {} } = contextData;

    // For resilience, we need to look for specific patterns in the data
    // Based on the sample data, we should look for:
    // - Positive/Negative explanatory style percentiles
    // - Resilience-related behaviors

    // Find resilience-related construct labels
    const positiveLabel = construct_label.find(item =>
      item.behavior_graph_key?.toLowerCase().includes('resilience') &&
      (item.behavior_graph_key?.toLowerCase().includes('positive') ||
        item.label?.toLowerCase().includes('positive'))
    );

    const negativeLabel = construct_label.find(item =>
      item.behavior_graph_key?.toLowerCase().includes('resilience') &&
      (item.behavior_graph_key?.toLowerCase().includes('negative') ||
        item.label?.toLowerCase().includes('negative'))
    );

    if (!positiveLabel && !negativeLabel) {
      return null;
    }

    const rawPositivePercentile = positiveLabel?.percentage;
    const rawNegativePercentile = negativeLabel?.percentage;

    const positivePercentile = rawPositivePercentile !== undefined ? Math.max(0, Math.min(100, safeNumber(rawPositivePercentile))) : undefined;
    const negativePercentile = rawNegativePercentile !== undefined ? Math.max(0, Math.min(100, safeNumber(rawNegativePercentile))) : undefined;

    if (positivePercentile === undefined && negativePercentile === undefined) {
      return null;
    }

    const resilienceBehaviors = Object.entries(behaviors).reduce((acc, [key, behaviorList]) => {
      if (Array.isArray(behaviorList) && (key.toLowerCase().includes('resilience') || behaviorList.length > 0)) {
        behaviorList.forEach(behavior => {
          if (behavior && behavior.label) {
            acc[behavior.label] = {
              human_readable_name: behavior.label,
              percentage: typeof behavior.percentage === 'number' ? behavior.percentage : 0,
              raw_value: behavior.raw_value !== undefined ? behavior.raw_value :
                (behavior.value !== undefined ? behavior.value : 'N/A')
            };
          }
        });
      }
      return acc;
    }, {});

    return {
      positivePercentile: positivePercentile ?? 0, // Use 0 if undefined for display purposes
      negativePercentile: negativePercentile ?? 0, // Use 0 if undefined for display purposes
      features: resilienceBehaviors
    };
  }, [contextData]);

  // Determine quadrant and get appropriate passage
  const getQuadrantInfo = (positive, negative) => {
    // Check if both percentiles fall in the middle range (45-55)
    if (positive >= 45 && positive <= 55 && negative >= 45 && negative <= 55) {
      return {
        quadrant: 'Balanced attributional style',
        color: '#9ca3af',
        passage: `Your results place you in the center of the resilience framework, indicating a balanced attributional style where you neither consistently credit yourself for successes nor blame yourself for failures. This middle ground suggests you have a moderate and flexible approach to interpreting both positive and negative events.

This balanced perspective can be both a strength and an area for growth. On one hand, you avoid the extremes of either excessive self-blame or unrealistic optimism, which can help you maintain steady emotional regulation. You're likely to approach challenges with realistic expectations and respond to both successes and setbacks with measured reactions.

However, this balanced style may sometimes limit your motivation and persistence. Research suggests that a slight optimistic bias in attributions can enhance performance and resilience. Consider developing more confidence in your abilities when things go well, while maintaining your balanced perspective on setbacks. This can help you build momentum from successes while staying grounded during challenges.`
      };
    } else if (positive >= 50 && negative >= 50) {
      return {
        quadrant: 'Optimistic attributional style for both positive and negative events',
        color: '#ddf',
        passage: `Your results place you in the upper-right quadrant of the resilience framework, indicating strong abilities in both "staying grounded after success" and "bouncing back after setbacks." You tend to view achievements as meaningful and reflective of your capabilities, while interpreting disappointments as temporary and specific rather than defining.

This optimistic explanatory style is associated with greater goal-setting, persistence, and adaptive coping—factors that contribute to sustained high performance. However, research also suggests that this mindset may occasionally lead to complacency if challenges are underestimated or not taken seriously enough.

To continue strengthening this skill, consider sharing your approach with others and setting stretch goals that push you beyond your comfort zone. Your demonstrated ability to positively reframe both successes and setbacks will be a valuable asset in dynamic and evolving environments.`
      };
    } else if (positive >= 50 && negative < 50) {
      return {
        quadrant: 'Optimistic attribution style for Positive events but pessimistic style for Negative events',
        color: '#fdd',
        passage: `Your results place you in the lower-right quadrant, showing solid—though sometimes uneven—strength on the two resilience dimensions. You take full credit for your successes, viewing them as reflections of your talent and consistency. You set ambitious goals for yourself and believe strongly in your ability to achieve them. However, when faced with failure, you may internalize the failure and struggle to recover emotionally.

Your motivation swings based on outcomes. You feel highly motivated and confident after success, but a single setback could significantly derail your momentum, causing doubt, hesitation, or even withdrawal. Over time, repeatedly attributing negative outcomes to personal, unchangeable flaws may lead to a sense of helplessness and reduced initiative, even in areas where you previously performed well.

To thrive, keep embracing the belief in your capabilities when things go well, but develop more forgiving, flexible ways of interpreting failure. With practice, you can maintain high standards without letting setbacks erode your confidence or motivation.`
      };
    } else if (positive < 50 && negative >= 50) {
      return {
        quadrant: 'Optimistic attributional style for negative events but pessimistic attributional style for positive events',
        color: '#ffd',
        passage: `Your profile falls in the upper-left quadrant, suggesting that you are resilient to failure but don't credit yourself for your success. You bounce back quickly from setbacks, often blaming external circumstances or temporary factors. However, you may struggle to internalize your accomplishments, attributing success to luck, help from others, or isolated factors rather than your own ability.

You are persistent but may lack confidence. You are likely to keep trying even after failures, since you don't see setbacks as personally defining. Yet, you may experience impostor syndrome, feeling you don't deserve your achievements or fearing you can't replicate them. You may seek frequent reassurance or external praise to compensate for not being able to recognize your own value. You are likely to habitually downplay your wins or deflect praise, often saying things like "I just got lucky" or "It wasn't a big deal." Over time, this can hinder leadership development, personal growth, and performance in high-stakes environments.

Your resilience is a tremendous strength but to reach your full potential, learn to internalize success without guilt or discomfort. Ask yourself, "What did I do that helped me succeed?" Keep a "success journal" where you regularly record achievements and reflect on your role in making them happen. Own the traits or skills that repeatedly show up in your successes. Respond to compliments with "Thank you" rather than deflecting. By gradually connecting your achievements to your consistent efforts and strengths, you can develop a more balanced self-concept—and ultimately thrive with both confidence and humility.`
      };
    } else {
      return {
        quadrant: 'Pessimistic attributional style for both positive and negative events',
        color: '#eee',
        passage: `Your profile falls in the lower-left quadrant, suggesting it can be hard to keep perspective after both triumphs and troubles. Good outcomes sometimes feel fleeting, as you are likely to downplay your role in success. On the other hand, setbacks may seem global or enduring, making it tempting to step back before fully exploring alternatives.

You might interpret setbacks as permanent or see wins as flukes rather than evidence of your ability. This pattern, often called a pessimistic explanatory style, has been linked to lower persistence, especially in challenging environments. However, this explanatory style may also result in investing more effort and energy into activity. For instance, research indicates that students who gave pessimistic explanations to their poor examination performance tended to make more plans to study for the next examination.

Remember, attributional styles are not fixed- they can be practiced and changed. Start small: after a win, try telling yourself "I earned that". After a setback, try "Next time, I'll do this differently". These small shifts help build resilience from inside out, making failure feel less defining and success feel more within reach.`
      };
    }
  };

  if (!resilienceData) {
    return (
      <div className="flex items-center justify-center h-64 bg-white rounded-lg border border-gray-200">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Loading resilience data...</p>
        </div>
      </div>
    );
  }

  const { positivePercentile, negativePercentile } = resilienceData;
  const quadrantInfo = getQuadrantInfo(positivePercentile, negativePercentile);

let QuadrantChartColor = {
    optimistic: '#E3F8EF',
    resilient: '#F4F7E2',
    pessimistic: '#E3F2F9',
    selfCritical: '#F9ECE3',
}


  return (
    <div className="flex flex-col gap-8">
      <style>{pulseAnimation}</style>

      <div className="flex flex-col sm:flex-row flex-wrap gap-6">
        {/* Left side - Quadrant Chart */}
        <div className="w-full md:w-1/2 bg-white p-4 sm:p-6 rounded-lg border border-gray-200 shadow-sm flex-1 min-w-[300px]">
          <div className="w-[90%] md:w-[70%] mx-auto">
            <h3 className="text-lg sm:text-xl md:!text-2xl font-semibold text-black mb-3 sm:!mb-4 text-center">
              Attributional Style Quadrant
            </h3>

            {/* Chart Container */}
            <div className="relative flex items-start aspect-square">
              {/* Y-axis label */}
              <div className="w-6 absolute top-1/2 -translate-y-1/2 -left-6 md:-left-8 flex items-center justify-center mr-2">
                <div className="transform -rotate-90 text-xs sm:text-sm md:text-lg font-medium text-gray-700 text-nowrap leading-snug">
                  Attribution style for negative events (percentile)
                </div>
              </div>

              {/* Y-axis scale labels */}
              <div className="flex-shrink-0 flex flex-col justify-between h-full mr-1 sm:mr-2">
                {[100, 75, 50, 25, 0].map((val) => (
                  <div key={val} className="text-xs sm:text-sm md:text-lg text-gray-600">
                    {val}
                  </div>
                ))}
              </div>
              <style>
                {
                  `
                  .graph-card{
                    transition: transform .8s; /* Animation */
                  }
                  .graph-card:hover {
                       transform: scale(1.6); 
                  }
                  `
                }
              </style>
              {/* Quadrant Chart */}
              <div className="relative w-full h-full border-2 border-gray-400 bg-white aspect-square min-w-[200px] graph-card">
                {/* Quadrants */}
                <div className={`absolute top-0 right-0 w-1/2 h-1/2 bg-[${QuadrantChartColor?.optimistic}]`}>
                  <div className="w-full h-full flex justify-center items-center p-1">
                    <span  ref={textRefs.optimistic} className={`text-xs sm:text-sm md:text-lg font-normal text-center text-gray-700 break-words leading-snug ${isYouTextOverlaping === 'optimistic' ? 'opacity-70' : ''}`}>
                      Optimistic Outlook for <br /> Events
                    </span>
                  </div>
                </div>
                <div className={`absolute top-0 left-0 w-1/2 h-1/2 bg-[${QuadrantChartColor?.resilient}]`}>
                  <div className="w-full h-full flex justify-center items-center p-1">
                    <span ref={textRefs.resilient} className={`text-xs sm:text-sm md:text-lg font-normal text-center text-gray-700 break-words leading-snug ${isYouTextOverlaping === 'resilient' ? 'opacity-70' : ''}`}>
                      Resilient but Dismissive <br /> of Success
                    </span>
                  </div>
                </div>
                <div className={`absolute bottom-0 right-0 w-1/2 h-1/2  bg-[${QuadrantChartColor?.selfCritical}]`}>
                  <div className="w-full h-full flex justify-center items-center p-1">
                    <span ref={textRefs.selfCritical} className={`text-xs sm:text-sm md:text-lg font-normal text-center text-gray-700 break-words leading-snug ${isYouTextOverlaping === 'selfCritical' ? 'opacity-70' : ''}`}>
                      High Standards but  <br /> Self-critical
                    </span>
                  </div>
                </div>
                <div className={`absolute bottom-0 left-0 w-1/2 h-1/2 bg-[${QuadrantChartColor?.pessimistic}]`}>
                  <div className="w-full h-full flex justify-center items-center p-1">
                    <span ref={textRefs.pessimistic} className={`text-xs sm:text-sm md:text-lg font-normal text-center text-gray-700 break-words leading-snug ${isYouTextOverlaping === 'pessimistic' ? 'opacity-70' : ''}`}>
                      Pessimistic Outlook <br /> for Events
                    </span>
                  </div>
                </div>

                {/* Neutral box */}
                <div
                  className="absolute border-2 border-gray-500 z-[19] cursor-help hover:opacity-60 text-center"
                  style={{
                    backgroundColor: '#53535347',
                    left: '39%',
                    bottom: '40%',
                    width: '22%',
                    height: '22%',
                    pointerEvents: 'auto'
                  }}
                  title="Neutral Attribution"
                >
                  <div className='absolute bg-[#FFFFFF] -bottom-3 left-1 text-xs  text-nowrap py-1 z-30 text-semibold opacity-100'><span className='p-0.5 text-black'>Flexible Attribution</span></div>
                </div>

                {/* Grid lines */}
                <div className="absolute top-1/2 left-0 w-full h-0.5 bg-black z-10"></div>
                <div className="absolute left-1/2 top-0 w-0.5 h-full bg-black z-10"></div>

                {/* User marker */}
                <div
                  className="absolute w-3 h-3 sm:w-5 sm:h-5 bg-blue-600 rounded-full border-2 border-white shadow-lg z-[12] you-marker"
                  style={{
                    left: `calc(${safeNumber(positivePercentile)}% - 8px)`,
                    bottom: `calc(${safeNumber(negativePercentile)}% - 8px)`
                  }}
                ></div>
                <div
                  ref={youRef}
                  className={`absolute text-xs sm:text-sm md:text-lg font-semibold text-blue-600 z-10`}
                  style={{
                    left: `calc(${Math.min(safeNumber(positivePercentile) + 0, 90)}% - 8px)`,
                    bottom: `calc(${Math.max(safeNumber(negativePercentile) - 4, 10)}% - 8px)`
                  }}
                >
                  You
                </div>
              </div>
            </div>

            {/* X-axis */}
            <div className="w-full sm:ml-6 mt-2">
              <div className="flex justify-between w-full mb-1 sm:mb-2 text-xs sm:text-sm md:text-lg text-gray-600">
                <span></span>
                <span>25</span>
                <span>50</span>
                <span>75</span>
                <span>100</span>
              </div>
              <div className="text-center w-full">
                <div className="text-xs sm:text-sm md:text-lg font-medium text-gray-700">
                  Attribution style for Positive events (percentile)
                </div>
              </div>
            </div>

            {/* Percentile boxes */}
            <div className="mt-4 sm:mt-6 sm:ml-6 flex flex-wrap gap-4 w-full">
              <div className="bg-[#1B75BC1A] p-3 rounded w-full flex-1 text-center">
                <div className="text-sm sm:text-lg font-medium text-gray-600 text-nowrap">Positive Attribution</div>
                <div className="text-lg sm:text-2xl font-bold text-[#1B75BC]">
                  {formatPercentage(positivePercentile)}th percentile
                </div>
              </div>
              <div className="bg-[#D726381A] p-3 rounded w-full flex-1 text-center">
                <div className="text-sm sm:text-lg font-medium text-gray-600 text-nowrap">Negative Attribution</div>
                <div className="text-lg sm:text-2xl font-bold text-[#D72638]">
                  {formatPercentage(negativePercentile)}th percentile
                </div>
              </div>
            </div>

            {/* Explanation */}
            <div className="mt-4 sm:mt-6 sm:ml-6 w-full p-3 bg-[#0000000A] rounded-lg border border-gray-200">
              <p className="text-xs sm:text-sm md:text-md text-gray-700 leading-relaxed">
                <strong>About Percentiles:</strong> Percentiles show how your score compares to a nationally representative sample of U.S. adults. For example, if you score in the 75th percentile, you scored higher than 75% of adults in the U.S.
              </p>
            </div>
          </div>
        </div>

        {/* Right side - Personalized Passage */}
        <div className="w-full md:w-1/2 bg-white p-4 sm:p-6 rounded-lg border border-gray-200 shadow-sm flex-1 min-w-[300px]">
          <div className="flex items-center mb-4">
            <h3 className="text-lg sm:text-xl md:text-2xl font-semibold text-black">
              {quadrantInfo.quadrant}
            </h3>
          </div>
          <div className="prose prose-sm max-w-none h-full flex flex-col justify-start">
            <div className="space-y-4 sm:space-y-6">
              {quadrantInfo.passage.split('\n\n').map((paragraph, index) => (
                <p key={index} className="text-gray-700 leading-relaxed text-sm sm:text-base md:text-xl break-words">
                  {paragraph}
                </p>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>

  );
};

export default ResilienceSection;


